<template>
  <div style="max-height:400px; overflow-y:auto;">
    <!-- Фильтр -->
    <input type="text"
           v-model="filter"
           placeholder="Поиск категории..."
           class="filter-text"
    />

    <!-- Дерево -->
    <ul style="list-style:none; padding-left:0;">
      <TreeNode
          v-for="node in filteredNodes"
          :key="node.id"
          :node="node"
          :rootNodes="nodes"
          :selectedKeys="localSelectionKeys"
          :openKeys="openKeys"
          :parentMap="parentMap"
          :filter-text="filter"
          @change-selection="onChangeSelection"
          @change-open-keys="updateOpenKeys"
      />
    </ul>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import TreeNode from './TreeNode.vue';

const props = defineProps({ nodes: Array, modelValue: [Array, Object] });
const emit = defineEmits(['update:modelValue']);

const filter = ref('');
const localSelectionKeys = reactive({}); // <-- объект для хранения выделения
const openKeys = reactive({});
const parentMap = {};

// --- Карта родителей ---
const buildParentMap = (nodes, parent = null) => {
  nodes.forEach(n => {
    if (parent) {
      parentMap[n.id] = parent;
    }

    if (n.children) {
      buildParentMap(n.children, n);
    }
  });
};

buildParentMap(props.nodes);

// --- Преобразование массива или объекта в {id: true} ---
const arrayToKeys = (val) => {
  const obj = {};

  if (Array.isArray(val)) {
    val.forEach(id => { obj[id] = true });
  } else if (val && typeof val === 'object') {
    Object.entries(val).forEach(([k,v]) => { obj[k] = v });
  }

  return obj;
};

// --- Инициализация localSelectionKeys ---
Object.assign(localSelectionKeys, arrayToKeys(props.modelValue));

// --- Синхронизация с modelValue ---
watch(props.modelValue, (val) => {
  Object.keys(localSelectionKeys).forEach(k => delete localSelectionKeys[k]);
  Object.assign(localSelectionKeys, arrayToKeys(val));
});

// --- Обновление выбранных ключей ---
const onChangeSelection = (newKeys) => {
  Object.keys(localSelectionKeys).forEach(k => delete localSelectionKeys[k]);
  Object.assign(localSelectionKeys, newKeys);

  // Отправляем наверх массив ID выбранных элементов
  emit('update:modelValue', selectedIds.value);
};

// --- Обновление открытых ключей ---
const updateOpenKeys = (newKeys) => {
  Object.keys(openKeys).forEach(k => delete openKeys[k]);
  Object.entries(newKeys).forEach(([k,v]) => openKeys[k] = v);
};

// --- Плоский массив всех выбранных id ---
const selectedIds = computed(() => {
  const result = new Set();

  const walk = (node) => {
    let hasSelected = !!localSelectionKeys[node.id];

    node.children?.forEach(child => {
      if (walk(child)) {
        hasSelected = true;
      }
    });

    if (hasSelected) {
      result.add(node.id);
    }

    return hasSelected;
  };

  props.nodes.forEach(walk);

  return Array.from(result);
});

// --- Фильтр с автоматическим раскрытием ---
const filteredNodes = computed(() => {
  const filterNode = (node) => {
    const match = node.label.toLowerCase().includes(filter.value.toLowerCase());

    if (!node.children || node.children.length === 0) {
      return match ? node : null;
    }

    const children = node.children.map(filterNode).filter(c => c);

    if (match || children.length) {
      if (filter.value) {
        openKeys[node.id] = true;
      }

      return { ...node, children };
    }

    return null;
  };

  return props.nodes.map(filterNode).filter(n => n);
});

// --- Сброс раскрытых узлов, если фильтр пустой ---
watch(filter, (val) => {
  if (!val) Object.keys(openKeys).forEach(k => delete openKeys[k]);
});
</script>

<style scoped src="./css/style.css"/>
